"use client"
import { useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import Accordion from "@mui/material/Accordion"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Tab from "@mui/material/Tab"
import Tabs from "@mui/material/Tabs"
import { styled as miuiStyled } from "@mui/material/styles"
import { useState } from "react"
import "swiper/css"
import {
  extractProperty,
  faqClickClose,
  faqClickOpen,
  filterClick,
  isHTMLString,
  replaceRichTextLinks,
} from "utils"
import { AccordionCloseIconSmall, AccordionExpandIconSmall, Container, NewText } from "../../ui"

const StyledSlideContainer = styled.div`
  background-color: white;
  width: 100%;
  max-width: 860px;
  margin: 0 auto;
  margin-top: 32px;
  padding: 32px 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 40px;
    padding: 20px 0;
  }
  .MuiTab-root {
    font-weight: 400;
    font-size: 32px;
    line-height: 44px;
    letter-spacing: 0px;
    text-align: right;
    vertical-align: middle;
    padding: 0;
    ${(props) => props.theme.breakpoints.up("md")} {
      font-size: 40px;
    }
  }
  .MuiAccordionSummary-root {
    background: ${(props) => props.theme.colors.faqRootBackground};
    height: 84px;
    ${(props) => props.theme.breakpoints.up("md")} {
      height: 67px;
    }
    border-radius: 8px;
    &::before {
      border-bottom: none;
    }
  }

  .MuiCollapse-root {
    border: 1px solid ${(props) => props.theme.colors.faqBorderColor};
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    ${(props) => props.theme.breakpoints.down("sm")} {
      /* background: linear-gradient(
        90deg,
        #3684cc -19.36%,
        #317bbf 35.51%,
        #1d4973 119.36%
      ); */
      background: white;
    }
    background: ${(props) => props.theme.colors.faqRootInsideBackground};
    &:hover {
      background: ${(props) => props.theme.colors.faqRootInsideBackgroundHover};
      cursor: pointer;
      p,
      li,
      h4 {
        color: ${(props) => props.theme.colors.faqRootInsideColorHover};
      }
    }
  }
`

const StyledAccordionSummary = styled(AccordionSummary)`
  &.MuiButtonBase-root {
    padding: 0;
    margin: 0 16px;
    &.Mui-expanded {
      border-radius: 0px;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
      border-bottom: 1px solid #f7f7f7;
      .MuiAccordionSummary-expandIconWrapper {
        transform: rotate(0deg);
      }
    }
  }
`

const StyledContentContainer = styled.div`
  padding: 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0px;
  }
  p {
    margin: 0px;
  }
`
const StyledTabstContainer = styled.div`
  .MuiTabs-scroller {
    overflow: auto !important;
    ::-webkit-scrollbar {
      display: none;
    }
  }
  .MuiTab-root {
    ${(props) => props.theme.breakpoints.up("md")} {
      max-width: 100%;
    }
  }
  p {
    margin: 0;
    max-width: 100%;
    white-space: normal;
  }
`

const StyledCustomTabPanel = styled.div`
  .MuiAccordionSummary-root:hover {
    svg path {
      stroke: ${(p) => p.theme.colors.faqTextColorHover};
    }
    .MuiTypography-root {
      color: ${(p) => p.theme.colors.faqTextColorHover};
    }
  }
`

const StyledAccordionDetails = miuiStyled(AccordionDetails)(() => ({
  padding: 10,
}))

function CustomTabPanel(props: any) {
  const { children, value, index, ...rest } = props

  return (
    <StyledCustomTabPanel
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ margin: 0, padding: 0, width: "100%" }}
      {...rest}
    >
      {value === index && children}
    </StyledCustomTabPanel>
  )
}

type Props = {
  data: any
  companyName?: string
}

export const FaqNew: React.FC<Props> = ({ data }) => {
  const blockTitle = extractProperty(data, "blockTitle")
  const content = extractProperty(data, "fAQMain").map((item: any) => ({
    tabName: extractProperty(item, "tabName"),
    faqs: extractProperty(item, "faqs").map((item: any) => ({
      accordionTitle: extractProperty(item, "accordionTitle"),
      accordionContent: extractProperty(item, "accordionContent"),
    })),
  }))

  const [value, setValue] = useState<number>(0)
  const [expanded, setExpanded] = useState<string | false>(false)

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setExpanded(false)
    filterClick(content[newValue].tabName, "תשובות שאלות ברכיב פילטר")
    setValue(newValue)
  }
  const theme = useTheme()

  return (
    <Container>
      <StyledSlideContainer>
        <StyledTabstContainer>
          {blockTitle && (
            <NewText
              desktopFontSize={40}
              desktopFontWeight="400"
              desktopLineHeight="44px"
              mobileFontSize={32}
              mobileFontWeight="400"
              mobileLineHeight={"44px"}
              component="h3"
              color={"black"}
              maxWidth={"100%"}
            >
              {blockTitle}
            </NewText>
          )}
          {content?.find((tab: any) => tab.tabName) && (
            <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
              {content.map((tab: any, index: number) => {
                const isRichText = isHTMLString(tab.tabName)
                if (isRichText) {
                  return (
                    <NewText
                      key={index}
                      desktopFontSize={40}
                      desktopFontWeight="400"
                      desktopLineHeight="44px"
                      mobileFontSize={32}
                      mobileFontWeight="400"
                      mobileLineHeight={"44px"}
                      component="div"
                      color={"black"}
                      dangerouslySetInnerHTML={{
                        __html: tab.tabName,
                      }}
                      maxWidth={"100%"}
                    />
                  )
                }
                return (
                  tab?.tabName && (
                    <Tab
                      key={index.toString()}
                      label={tab.tabName}
                      style={{ textAlign: content.length > 1 ? "center" : "right" }}
                    />
                  )
                )
              })}
            </Tabs>
          )}
        </StyledTabstContainer>
        <StyledContentContainer>
          {content.map((tab: any, index: number) => (
            <CustomTabPanel key={index.toString()} value={value} index={index}>
              {tab.faqs.map((i: any, k: number) => {
                const parsedContent = replaceRichTextLinks(i.accordionContent)
                return (
                  <Accordion
                    key={k.toString()}
                    style={{
                      padding: 0,
                      margin: 0,
                      marginBlockStart: 32,
                      boxShadow: theme.colors.faqBoxShadow,
                      border: "none",
                      borderRadius: 8,
                    }}
                    expanded={expanded === `acc-${k}`}
                    onChange={() => {
                      setExpanded((prev) => {
                        if (prev === `acc-${k}`) {
                          faqClickClose(i.accordionTitle, "תשובות שאלות ברכיב פילטר", "close")
                          return false
                        } else {
                          faqClickOpen(i.accordionTitle, "תשובות שאלות ברכיב פילטר", "open")
                          return `acc-${k}`
                        }
                      })
                    }}
                    sx={{
                      "&:before": {
                        display: "none",
                      },
                    }}
                  >
                    <StyledAccordionSummary
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                      expandIcon={
                        expanded === `acc-${k}` ? (
                          <AccordionCloseIconSmall />
                        ) : (
                          <AccordionExpandIconSmall />
                        )
                      }
                      style={{
                        outline: "none",
                      }}
                      sx={{
                        "&:before":
                          expanded === `acc-${k}`
                            ? {
                                // eslint-disable-next-line quotes
                                content: '" "',
                                textDecoration: "none",
                                position: "absolute",
                                width: "calc(100% - 32px)",
                                height: "100%",
                                left: "16px",
                                borderBottom: "solid 1px #F7F7F7",
                              }
                            : {
                                display: "none",
                              },
                      }}
                    >
                      <NewText
                        desktopFontSize={16}
                        desktopFontWeight="600"
                        desktopLineHeight="23px"
                        mobileFontSize={14}
                        mobileFontWeight="600"
                        mobileLineHeight={"20px"}
                        component="p"
                        color={
                          expanded === `acc-${k}`
                            ? theme.colors.faqTextColorExpanded
                            : theme.colors.faqTextColor
                        }
                      >
                        {i.accordionTitle}
                      </NewText>
                    </StyledAccordionSummary>
                    <StyledAccordionDetails>
                      <NewText
                        desktopFontSize={16}
                        desktopFontWeight="400"
                        desktopLineHeight="23px"
                        mobileFontSize={14}
                        mobileFontWeight="400"
                        mobileLineHeight={"20px"}
                        component="div"
                        color={"black"}
                        dangerouslySetInnerHTML={{ __html: parsedContent }}
                        maxWidth={"800px"}
                        marginTop={"16px"}
                        marginBottom={"16px"}
                      />
                    </StyledAccordionDetails>
                  </Accordion>
                )
              })}
            </CustomTabPanel>
          ))}
        </StyledContentContainer>
      </StyledSlideContainer>
    </Container>
  )
}

export default FaqNew
